import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UseType } from 'go-modules/models/use-type/use-type.interface';
import type { SignupOrg } from '@dashboard/app/interfaces/sign-up/signup-orgs-response';
import { BehaviorSubject, EMPTY, from, Observable, of, Subject } from 'rxjs';
import { SignUpService } from '@go-modules/services/sign-up/sign-up.service';
import type { EmailAndPasswordData } from '@dashboard/app/interfaces/sign-up/email-and-password-data';
import { catchError, filter, first, ignoreElements, map, share, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import type { RequestValidationError } from '@go-modules/interfaces/request-validation-error';
import type { OrgCreateData } from '@dashboard/app/interfaces/sign-up/org-create-data';
import { ROUTE_NAMES } from '@go-modules/services/router/router.service';
import type { SignupRequest } from '@dashboard/app/interfaces/sign-up/signup-request';
import type { AllowedOrg, EmailVerificationResponse } from '@go-modules/enums/email-verification-response';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { SignupUtil } from '../signup-util';
import type { CourseInvite, SsoData } from '@dashboard/app/components/signup/course-invite';
import type { AuthResponseSuccessDataInterface } from '@go-modules/services/auth/interfaces/auth-response-success.interface';
import type { StudentRegisterRequest } from '@go-modules/interfaces';
import { SignupMode } from '@dashboard/app/components/signup/signup-mode';
import type { JobTitle } from '../job-title';

export const NETWORK_LOST_TRANSLATE_KEY = 'sign-up-form-validation-connection-lost';
export const DUPLICATE_EMAIL_TRANSLATE_KEY = 'sign-up-form-validation-duplicate-user-email';
export const DUPLICATE_ORG_NAME_TRANSLATE_KEY = 'sign-up-form-validation-duplicate-org-name';
export const UNKNOWN_ERROR_TRANSLATE_KEY = 'sign-up-form-validation-unknown-issue';

export interface StateGoNamedParameters {
	to: ROUTE_NAMES;
	params?: {email: string, password: string};
}

@Component({
	selector: 'account-create',
	templateUrl: './account-create.component.html',
	styleUrls: ['./account-create.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AccountCreateComponent implements OnChanges, OnInit {
	public static readonly NAME_MAX_LENGTH = 40;

	@Input()
	public emailAndPasswordData: EmailAndPasswordData;
	@Input()
	public org: SignupOrg | OrgCreateData | AllowedOrg;
	@Input()
	public emailVerificationData: EmailVerificationResponse;
	@Input()
	public courseInvite: CourseInvite;
	@Input()
	public ssoData: SsoData;
	@Input()
	public infoKey: string|null;
	@Input()
	public mode: SignupMode;
	@Output()
	public onBack$ = new EventEmitter<void>(); // eslint-disable-line @angular-eslint/no-output-on-prefix
	@Output()
	public onNavigate$ = new EventEmitter<StateGoNamedParameters>(); // eslint-disable-line @angular-eslint/no-output-on-prefix
	@Output()
	public reloadOrgs$ = new EventEmitter<any>();

	public form = new UntypedFormGroup({
		phoneNumber: new UntypedFormControl(null, []),
		useType: new UntypedFormControl(null),
		jobRole: new UntypedFormControl(null)
	});

	public SIGNUPMODES = SignupMode;
	public accountCreate$ = new Subject<void>();
	public useTypes$: Observable<UseType[]>;
	public submitting$: Observable<boolean> = of(false);
	public errorMessage$: Observable<string> = of(null);
	public jobRolesCategorized$: Observable<Record<string, JobTitle[]>>;
	public jobRoles$: Observable<JobTitle[]>;
	public selectedOrgType$ = new BehaviorSubject<number>(null);
	private errorSubject$ = new Subject<HttpErrorResponse>();

	constructor (
		private signUpService: SignUpService,
		private authService: NgxAuthService
	) {}

	public get requiresAccountInfo (): boolean {
		if (this.mode === SignupMode.GUEST_REVIEW) return false;
		return SignupUtil.requiresAccountInfo(this.courseInvite);
	}

	public get firstName (): string {
		return this.infoKey ? this.form.value.firstName : this.emailAndPasswordData.firstName;
	}

	public get lastName (): string {
		return this.infoKey ? this.form.value.lastName : this.emailAndPasswordData.lastName;
	}

	private static errorHandler (err: HttpErrorResponse): string {
		if (err.status === 0) {
			return NETWORK_LOST_TRANSLATE_KEY;
		}
		if (err.status === 422) {
			const errors: RequestValidationError = err.error;
			if (errors.failed['user.email']?.includes('Unique')) {
				return DUPLICATE_EMAIL_TRANSLATE_KEY;
			}
			if (errors.failed['org.name']?.includes('Unique')) {
				return DUPLICATE_ORG_NAME_TRANSLATE_KEY;
			}
		}

		return UNKNOWN_ERROR_TRANSLATE_KEY;
	}

	private static isExistingOrg (org: SignupOrg | OrgCreateData): org is SignupOrg | AllowedOrg {
		return 'group_id' in org;
	}

	public ngOnInit (): void {
		this.jobRolesCategorized$ = this.signUpService.getJobTitles().pipe(
			map((jobTitles) => {
				const categorized: Record<string, JobTitle[]> = {};

				for (const jobTitle of jobTitles) {
					for (const orgType of jobTitle.org_types) {
						if (!categorized[orgType.id]) {
							categorized[orgType.id] = [];
						}

						categorized[orgType.id].push(jobTitle);
					}
				}

				return categorized;
			}),
			catchError((err: HttpErrorResponse) => {
				this.errorSubject$.next(err);
				return of({});
			}),
			shareReplay(1)
		);

		this.errorMessage$ = this.errorSubject$.pipe(
			map(AccountCreateComponent.errorHandler),
			startWith(null)
		);

		this.jobRoles$ = this.selectedOrgType$.pipe(
			switchMap((orgTypeId) =>
				this.jobRolesCategorized$.pipe(
					map((categorized) => {
						if (!orgTypeId) return [];

						return categorized[orgTypeId];
					})
				)
			)
		);

		// If using SSO provider
		if (this.infoKey) {
			this.form.addControl('firstName', new UntypedFormControl(this.ssoData.first_name, [
				Validators.required,
				Validators.maxLength(AccountCreateComponent.NAME_MAX_LENGTH)
			]));
			this.form.addControl('lastName', new UntypedFormControl(this.ssoData.last_name, [
				Validators.required,
				Validators.maxLength(AccountCreateComponent.NAME_MAX_LENGTH)
			]));
		}

		if (this.requiresAccountInfo) {
			this.useTypes$ = this.signUpService.getUseTypes();
			this.form.controls.useType.addValidators(Validators.required);
			this.form.controls.jobRole.addValidators(Validators.required);
		}

		if (this.mode === SignupMode.GUEST_REVIEW) {
			this.form.removeControl('phoneNumber');
			this.form.removeControl('useType');
			this.form.removeControl('jobRole');
		}

		// Create a pipe from the starting click to the ending redirect to dashboard
		this.accountCreate$.pipe(
			filter(() => this.form.valid),
			switchMap(() => {
				const createAccount$ = this.requiresAccountInfo ?
					this.signUpService.createAccount(this.getSignupRequestData()).pipe(share()) :
					this.createStudentAccount().pipe(share());
				// Do the http call and share the result (make the observer hot)

				// Create a pipe that only pays attention to errors and displays them
				this.errorMessage$ = createAccount$.pipe(
					ignoreElements(),
					catchError((err: HttpErrorResponse) => of(err)),
					map(AccountCreateComponent.errorHandler)
				);

				// In the case that the errorMessage is duplicate org, we need to reload the orgs
				this.errorMessage$.pipe(
					filter((msg) => msg === DUPLICATE_ORG_NAME_TRANSLATE_KEY)
				).subscribe(this.reloadOrgs$);

				// Create a pipe that handles while we are submitting.
				// Normally we'd set it to false once the request is finished, but we don't want
				// the user to be able to push the button again while the dashboard page is loading,
				// so we'll only set it back to false if there is an error.
				this.submitting$ = createAccount$.pipe(
					ignoreElements(),
					startWith(true),
					catchError(() => of(false))
				);

				// And return the pipe that ignores errors
				return createAccount$.pipe(
					catchError(() => EMPTY)
				);
			}),
			first(),
			map((): StateGoNamedParameters => {
				return this.mode === SignupMode.GUEST_REVIEW ? {to: 'SSOCOMPLETE' as ROUTE_NAMES} : {to: ROUTE_NAMES.DASHBOARD_ROOT};
			})
		).subscribe(this.onNavigate$);

		// If they go back clear any error messages
		this.onBack$.subscribe(() => {
			this.errorMessage$ = of(null);
		});
	}

	public ngOnChanges (changes: SimpleChanges): void {
		if ('org' in changes && changes.org.currentValue != null) {
			const org: AccountCreateComponent['org'] = changes.org.currentValue;
			this.setSelectedOrgType(org);
		}
	}

	public resolveError (errorKey) {
		switch (errorKey) {
			case NETWORK_LOST_TRANSLATE_KEY:
				// Do nothing
				return;
			case DUPLICATE_EMAIL_TRANSLATE_KEY:
				// They already exist, login
				this.onNavigate$.next({to: ROUTE_NAMES.AUTH_LOGIN});
				return;
			case DUPLICATE_ORG_NAME_TRANSLATE_KEY:
				// Their org already exists, send them back to select it.
				this.onBack$.next(null);
				return;
			default:
				// We don't know what went wrong. Start over
				this.onNavigate$.next({to: ROUTE_NAMES.AUTH_SIGNUP});
		}
	}

	public createStudentAccount (): Observable<AuthResponseSuccessDataInterface> {
		const data: StudentRegisterRequest = {
			first_name: this.firstName,
			last_name: this.lastName,
			course_code: this.courseInvite.course?.uuid,
			invite_uuid: this.courseInvite.invite?.uuid,
			accepted_terms_at: new Date()
		};

		data['info-key'] = this.infoKey;

		return from(this.authService.registerStudent(data));
	}

	private setSelectedOrgType (org: SignupOrg | OrgCreateData | AllowedOrg) {
		const type = AccountCreateComponent.isExistingOrg(org) ?
			org.org_type_id :
			org.orgSettings.org_type_id;
		this.selectedOrgType$.next(type);
	}

	private getSignupRequestData (): SignupRequest {
		const commonUserData = {
			first_name: this.firstName,
			last_name: this.lastName,
			phone_number: this.form.controls.phoneNumber.value || null,
			accepted_terms_at: new Date()
		};

		const commonData = {
			org: AccountCreateComponent.isExistingOrg(this.org) ?
				{org_id: this.org.group_id} :
				this.org,
			account: {
				use_type_id: this.form.controls.useType.value.use_type_id
			},
			role: this.form.controls.jobRole.value
		};

		if (this.infoKey) {
			return {
				'info-key': this.infoKey,
				'user': commonUserData,
				...commonData
			};
		}

		return {
			user: {
				email: this.emailAndPasswordData.email,
				password: this.emailAndPasswordData.password,
				...commonUserData
			},
			email_signature: this.emailVerificationData.email_signature,
			...commonData
		};
	}
}
